// Vital-sign trends (milestone L3.3): for each measurement, the 15-minute buckets of the values
// the source sent, as a band from lowest to highest and a line through the latest value. No
// thresholds, colours for "abnormal", scores or predictions (CLAUDE.md hard rule 3). NEWS2 is
// not drawn: it is shown only as the score the observations system sent.

import type { TrendSeries } from '@diagnuvo/domain';
import { clockTime } from '../board-state.ts';

/** Display names and order; any other code follows, by its own name. */
const LABELS: Readonly<Record<string, string>> = {
  HR: 'Heart rate',
  RR: 'Respiratory rate',
  SPO2: 'SpO2',
  SBP: 'Systolic BP',
  DBP: 'Diastolic BP',
  TEMP: 'Temperature',
  GCS: 'GCS',
};
const ORDER = Object.keys(LABELS);
const HIDDEN = new Set(['NEWS2']);

const W = 240;
const H = 44;
const PAD = 3;

/** The SVG path of the band (lowest to highest) and of the latest-value line. Pure. */
export function sparkline(series: TrendSeries): { band: string; line: string } {
  const points = series.points;
  if (points.length === 0) return { band: '', line: '' };
  const t0 = Date.parse(points[0]?.at ?? '');
  const t1 = Date.parse(points.at(-1)?.at ?? '');
  const lo = Math.min(...points.map((p) => p.min));
  const hi = Math.max(...points.map((p) => p.max));
  const x = (at: string) =>
    t1 === t0 ? W / 2 : PAD + ((Date.parse(at) - t0) / (t1 - t0)) * (W - 2 * PAD);
  const y = (v: number) => (hi === lo ? H / 2 : H - PAD - ((v - lo) / (hi - lo)) * (H - 2 * PAD));
  const f = (n: number) => n.toFixed(1);
  const top = points.map((p) => `${f(x(p.at))},${f(y(p.max))}`);
  const bottom = [...points].reverse().map((p) => `${f(x(p.at))},${f(y(p.min))}`);
  return {
    band: `M${top.join('L')}L${bottom.join('L')}Z`,
    line: `M${points.map((p) => `${f(x(p.at))},${f(y(p.last))}`).join('L')}`,
  };
}

const label = (code: string) => LABELS[code] ?? code;
const unit = (u: string) => (u === '' ? '' : u === '%' ? '%' : ` ${u}`);

export function VitalTrends({ series }: { series: TrendSeries[] }) {
  const shown = series
    .filter((s) => !HIDDEN.has(s.code) && s.points.length > 0)
    .sort((a, b) => {
      const ia = ORDER.indexOf(a.code);
      const ib = ORDER.indexOf(b.code);
      if (ia !== ib) return (ia === -1 ? ORDER.length : ia) - (ib === -1 ? ORDER.length : ib);
      return label(a.code).localeCompare(label(b.code));
    });
  if (shown.length === 0) {
    return <p className="muted">No vital-sign values recorded in this window.</p>;
  }
  return (
    <ul className="trends">
      {shown.map((s) => {
        const first = s.points[0];
        const last = s.points.at(-1);
        if (first === undefined || last === undefined) return null;
        const lo = Math.min(...s.points.map((p) => p.min));
        const hi = Math.max(...s.points.map((p) => p.max));
        const { band, line } = sparkline(s);
        const name = label(s.code);
        return (
          <li key={s.code} data-vital={s.code}>
            <div className="trend-head">
              <span className="trend-label">{name}</span>
              <span className="trend-latest">
                {last.last}
                {unit(s.unit)}
              </span>
            </div>
            <svg
              className="trend-chart"
              viewBox={`0 0 ${W} ${H}`}
              role="img"
              aria-label={`${name}: values as sent from ${clockTime(first.at)} to ${clockTime(last.at)}, lowest ${lo}, highest ${hi}, latest ${last.last}${unit(s.unit)}`}
            >
              <path className="trend-band" d={band} />
              <path className="trend-line" d={line} />
            </svg>
            <div className="trend-foot muted">
              <span>{clockTime(first.at)}</span>
              <span>
                range {lo}–{hi}
                {unit(s.unit)}
              </span>
              <span>{clockTime(last.at)}</span>
            </div>
          </li>
        );
      })}
    </ul>
  );
}
